import type {
  CollectionsAnalyticsViewADto,
  GetDigitalAssetCollectionsParamsADto,
} from '@cohort/admin-schemas/digitalAssetCollection';
import AdminPage from '@cohort/merchants/components/AdminPage';
import Button from '@cohort/merchants/components/buttons/Button';
import EmptyState from '@cohort/merchants/components/EmptyState';
import SingleChoiceFilter from '@cohort/merchants/components/filters/SingleChoiceFilter';
import LoadingState from '@cohort/merchants/components/LoadingState';
import {ConfirmationModal} from '@cohort/merchants/components/modals/ConfirmationModal';
import SearchInput from '@cohort/merchants/components/SearchInput';
import {useDigitalAssetCollectionsAnalyticsView} from '@cohort/merchants/hooks/api/DigitalAssetCollections';
import {useCurrentCampaign} from '@cohort/merchants/hooks/contexts/currentCampaign';
import useEntitySearchFilter from '@cohort/merchants/hooks/entitySearchFilter';
import {sortFilter} from '@cohort/merchants/hooks/filters/digitalAssetCollectionsListFilters';
import {useUserSessionStore} from '@cohort/merchants/hooks/stores/userSession';
import {notify} from '@cohort/merchants/hooks/toast';
import {getCampaignsRoute, getDigitalAssetsRoute} from '@cohort/merchants/lib/Pages';
import DigitalAssetCollectionsList from '@cohort/merchants/pages/campaigns/campaign/edit/reward/DigitalAssetCollectionsList';
import type {FormStepProps} from '@cohort/merchants/pages/campaigns/campaign/edit/utils';
import {zodResolver} from '@hookform/resolvers/zod';
import {Plus} from '@phosphor-icons/react';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {isEmpty} from 'remeda';
import {match, P} from 'ts-pattern';
import {z} from 'zod';

const PAGE_SIZE = 100;

export const CampaignRewardStepSchema = z
  .object({
    digitalAssetCollectionId: z.string().optional(),
    digitalAssetCollection: z.any().nullable(), // Computed field, only needed for validation
  })
  .superRefine(({digitalAssetCollectionId, digitalAssetCollection}, ctx) => {
    if (!digitalAssetCollectionId && !digitalAssetCollection) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorRequired',
        path: ['digitalAssetCollectionId'],
      });
    }
  });

export type CampaignRewardStepValues = z.infer<typeof CampaignRewardStepSchema>;

const CampaignRewardStep: React.FC<
  FormStepProps & {
    enableDigitalAssetCreation?: boolean;
  }
> = ({
  updateCampaign,
  setStepDirty,
  setStepSaveBtn,
  onStepValidated,
  enableDigitalAssetCreation = true,
}): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const merchantId = useUserSessionStore(store => store.merchantId!);
  const campaign = useCurrentCampaign();
  const {t} = useTranslation('pages', {
    keyPrefix: 'campaigns.campaign.edit.campaignRewardStep',
  });
  const navigate = useNavigate();
  const [sort, setSort] = useState<GetDigitalAssetCollectionsParamsADto['orderBy']>('updatedAt');
  const [search, setSearch] = useState('');
  const [confirmationModal, setConfirmationModal] = useState(false);

  // i18nOwl-ignore [sortBy.internalName, sortBy.updatedAt]
  const sortingOptions = sortFilter.map(filter => ({
    value: filter,
    label: t(`sortBy.${filter}`),
    active: sort === filter,
  }));

  const {data, isFetched} = useDigitalAssetCollectionsAnalyticsView(merchantId, {
    page: 1,
    pageSize: PAGE_SIZE,
    orderBy: sort,
  });
  const [, digitalAssetCollections] = data ?? [];
  const filteredDigitalAssetCollections = useEntitySearchFilter(
    digitalAssetCollections || [],
    'internalName',
    search
  );

  const {
    register,
    handleSubmit,
    formState: {errors, isDirty},
  } = useForm<CampaignRewardStepValues>({
    defaultValues: {
      digitalAssetCollectionId: campaign.digitalAssetCollection?.id,
      digitalAssetCollection: campaign.digitalAssetCollection ?? undefined,
    },
    resolver: zodResolver(CampaignRewardStepSchema),
  });

  useEffect(() => {
    setStepDirty(isDirty);
  }, [isDirty, setStepDirty]);

  useEffect(() => {
    setStepSaveBtn(
      <Button
        variant="secondary"
        onClick={() => {
          notify('success', t('notificationSuccess'));
          navigate(getCampaignsRoute().path);
        }}
      >
        {t('saveBtn')}
      </Button>
    );
  }, [t, navigate, setStepSaveBtn]);

  const digitalAssetsCollectionGuard = (
    digitalAssetsCollections: CollectionsAnalyticsViewADto[] | undefined
  ): digitalAssetsCollections is CollectionsAnalyticsViewADto[] =>
    digitalAssetsCollections !== undefined && digitalAssetsCollections.length > 0;

  const content = match({filteredDigitalAssetCollections, isFetched})
    .with({isFetched: false}, () => <LoadingState />)
    .with(
      {
        filteredDigitalAssetCollections: P.select(P.when(digitalAssetsCollectionGuard)),
      },
      digitalAssetCollections => (
        <DigitalAssetCollectionsList
          digitalAssetCollections={digitalAssetCollections}
          register={register}
        />
      )
    )
    .otherwise(() => (
      <EmptyState
        title={t('titleEmpty')}
        description={t('descriptionEmpty')}
        cta={
          <Button
            onClick={() => setConfirmationModal(true)}
            data-testid="create-digital-asset-collection-empty-state"
          >
            <Plus className="-ml-1 mr-2 h-5 w-5" />
            {t('btnCreateDigitalAsset')}
          </Button>
        }
      />
    ));

  const headerConfig = {
    title: t('title'),
    subtitle: t('subtitle'),
    topRightElements: enableDigitalAssetCreation
      ? [
          <Button key="create-digital-asset-btn" onClick={() => setConfirmationModal(true)}>
            {t('btnCreateDigitalAsset')}
          </Button>,
        ]
      : undefined,
  };

  return (
    <AdminPage header={headerConfig}>
      <form
        className="space-y-6"
        id="campaign-reward-step"
        onSubmit={handleSubmit(async data => {
          await updateCampaign(data);
          onStepValidated();
        })}
      >
        <div className="flex items-center justify-between">
          <SearchInput value={search} onChange={setSearch} />
          <SingleChoiceFilter
            options={sortingOptions}
            onChange={option =>
              setSort(option.value as GetDigitalAssetCollectionsParamsADto['orderBy'])
            }
            prefix={t('sortByLabel')}
          />
        </div>
        {content}
        {!isEmpty(errors) && (
          <p className="text-sm text-red-500" data-testid="digitalAssetCollectionId-error">
            {t('digitalAssetCollectionIdRequired')}
          </p>
        )}
      </form>
      {confirmationModal && (
        <ConfirmationModal
          title={t('confirmModalTitle')}
          content={t('confirmModalContent')}
          submitButtonText={t('confirmModalSubmitBtn')}
          show={true}
          onClose={() => setConfirmationModal(false)}
          onSubmit={() => navigate(`${getDigitalAssetsRoute().path}?create=true`)}
        />
      )}
    </AdminPage>
  );
};

export default CampaignRewardStep;
